import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  DateField,
  Field,
  SearchSelect,
  SideDrawer,
  TextArea,
  acceptAttribute,
  buttonClasses,
  linkTextClasses,
} from '@evogard/ui';
import type { CompensationDebtWrite, CompensationSummary } from '@evogard/contract';
import {
  COMPENSATION_DEBT_KINDS,
  COMPENSATION_DISCOUNT_KINDS,
  DEPRECIATION_REASONS,
  EXPENSE_STAGE_TONES,
  PARTICIPATION_DEDUCTION,
  compensationDebtProblem,
  deductionKindLabel,
  expenseStageLabel,
  format,
  formatInstant,
  fromColumn,
  isCompensationDebtStatus,
  isCompensationDiscountKind,
  moneyInputText,
  parseMoneyInput,
  toColumn,
  type ExpenseStage,
  type Money,
} from '@evogard/domain';
import { claimFileUrl, useCompensationDebts } from './useDesk.js';
import { Section, Wait } from './RepairBlock.js';

type Debt = CompensationSummary['deductions'][number];

const optionsOf = (o: Record<string, string>) =>
  Object.entries(o).map(([value, label]) => ({ value, label }));

/** Os tipos que o "Incluir desconto" oferece: os que não se pagam a ninguém. */
const DISCOUNT_OPTIONS = COMPENSATION_DISCOUNT_KINDS.map((k) => ({
  value: k,
  label: COMPENSATION_DEBT_KINDS[k],
}));

/** O que o formulário do Pagamento de Débito pode fazer com os débitos. */
export interface DebtActions {
  /** Abre a gaveta "Lançar despesa" — a mesma da aba Despesas. Nulo: sem a permissão. */
  onLaunchExpense: (() => void) | null;
  /**
   * Abre a gaveta do desconto (29/10/2026, pedido do dono): `null` para incluir,
   * o débito para corrigir. A mesma moldura da gaveta de despesa.
   */
  onOpenDiscount: (debt: Debt | null) => void;
  /** O formulário se edita: a fase atual, o sinistro ativo, quem pode mover. */
  editable: boolean;
}

/**
 * Os débitos da Indenização (§5.9).
 *
 * **Desde 29/10/2026 (decisão do dono) são dois caminhos**, e os dois moram no
 * FORMULÁRIO do Pagamento de Débito:
 *
 * - **O que se paga a um credor** (IPVA, multa, gravame…) é uma DESPESA: entra
 *   pelo botão "Lançar despesa", que abre a mesma gaveta da aba Despesas, vai
 *   ao Financeiro e desconta da FIPE desde o lançamento. O débito ligado a ela
 *   não muda por aqui — o valor e a situação são do Financeiro.
 * - **O que só desconta** (mensalidade, despachante, depreciação) não tem a
 *   quem pagar: entra por "Incluir desconto", e se corrige e se dispensa aqui.
 *
 * Sem `actions`, é a lista só de leitura da gaveta da conta (o modal do bloco):
 * ali não se inclui mais nada — é a mesma lógica das tentativas de contato,
 * que se registram no formulário.
 *
 * A PI não aparece aqui: ela vem da Entrada da Análise, e a conta já a mostra.
 * O débito não se apaga — o que entrou por engano é dispensado.
 */
export function CompensationDebts({
  claimId,
  summary: s,
  actions,
}: {
  claimId: number;
  summary: CompensationSummary;
  /** Os botões do formulário. Sem eles, só leitura. */
  actions?: DebtActions;
}) {
  const editable = !!actions?.editable && s.debtsEditable;
  const debts = s.deductions.filter((d) => d.kind !== PARTICIPATION_DEDUCTION);
  const mutations = useCompensationDebts(claimId);
  const error =
    mutations.setStatus.error ??
    mutations.attachReceipt.error ??
    mutations.archiveReceipt.error;

  return (
    <Section
      title="Débitos"
      aside={actions ? 'pago vira despesa · o resto só desconta' : 'só leitura'}
    >
      {error && <Alert tone="erro">{error.message}</Alert>}
      {debts.length > 0 && (
        <ul className="flex flex-col rounded-borda border border-borda">
          {debts.map((d) => (
            <DebtRow
              key={d.id}
              claimId={claimId}
              debt={d}
              editable={editable}
              busy={
                (mutations.setStatus.isPending &&
                  mutations.setStatus.variables?.debtId === d.id) ||
                (mutations.attachReceipt.isPending &&
                  mutations.attachReceipt.variables?.debtId === d.id) ||
                (mutations.archiveReceipt.isPending &&
                  mutations.archiveReceipt.variables?.debtId === d.id)
              }
              onEdit={() => actions?.onOpenDiscount(d)}
              onStatus={(status) => mutations.setStatus.mutate({ debtId: d.id, status })}
              onAttach={(file) => mutations.attachReceipt.mutate({ debtId: d.id, file })}
              onArchive={(documentId) =>
                mutations.archiveReceipt.mutate({ debtId: d.id, documentId })
              }
            />
          ))}
        </ul>
      )}
      {debts.length === 0 && (
        <Wait>
          {actions
            ? 'Nenhum débito. O que se paga a um credor (IPVA, multa, gravame…) entra como despesa; mensalidade, despachante e depreciação, como desconto.'
            : 'Nenhum débito. Os débitos se lançam no formulário do Pagamento de Débito.'}
        </Wait>
      )}
      {editable && actions && (
        <div className="flex flex-wrap gap-2">
          {actions.onLaunchExpense && (
            <Button icon="mais" size="sm" onClick={actions.onLaunchExpense}>
              Lançar despesa
            </Button>
          )}
          <Button variant="contorno" size="sm" onClick={() => actions.onOpenDiscount(null)}>
            Incluir desconto
          </Button>
        </div>
      )}
      {actions && !actions.onLaunchExpense && editable && (
        <p className="text-xs text-texto-suave">
          Lançar despesa pede a permissão de lançar despesa da Indenização.
        </p>
      )}
      {!actions && debts.length > 0 && (
        <p className="text-xs text-texto-suave">
          Os débitos se lançam no formulário do Pagamento de Débito; os pagos a um credor
          seguem na aba Despesas.
        </p>
      )}
    </Section>
  );
}

/**
 * Incluir ou corrigir um desconto, na gaveta da mesa (29/10/2026, pedido do
 * dono): a mesma moldura da gaveta "Lançar despesa", para os dois caminhos dos
 * débitos se abrirem do mesmo jeito.
 */
export function DiscountDrawer({
  claimId,
  debt,
  onClose,
}: {
  claimId: number;
  /** O desconto em correção, ou `null` para incluir. */
  debt: Debt | null;
  onClose: () => void;
}) {
  const mutations = useCompensationDebts(claimId);
  const mutation = debt ? mutations.update : mutations.create;
  return (
    <SideDrawer
      eyebrow={`Indenização do aviso #${claimId} · Pagamento de Débito`}
      title={debt ? 'Corrigir o desconto' : 'Incluir desconto'}
      onClose={onClose}
      closeLabel="Fechar o desconto"
    >
      <DiscountForm
        debt={debt}
        busy={mutation.isPending}
        error={mutation.error?.message ?? null}
        onCancel={onClose}
        onSubmit={(body) =>
          debt
            ? mutations.update.mutate({ debtId: debt.id, body }, { onSuccess: onClose })
            : mutations.create.mutate(body, { onSuccess: onClose })
        }
      />
    </SideDrawer>
  );
}

/** A fase da despesa no Financeiro, como o supervisor a vê (o atraso mascarado). */
export function ExpenseStageTag({ stage }: { stage: ExpenseStage }) {
  return (
    <Badge tone={EXPENSE_STAGE_TONES[stage]}>
      despesa · {expenseStageLabel(stage, 'supervisor').toLowerCase()}
    </Badge>
  );
}

/** Um débito: o que é, quanto, de onde vem, os comprovantes e os atos. */
function DebtRow({
  claimId,
  debt: d,
  editable,
  busy,
  onEdit,
  onStatus,
  onAttach,
  onArchive,
}: {
  claimId: number;
  debt: Debt;
  editable: boolean;
  busy: boolean;
  onEdit: () => void;
  onStatus: (status: string) => void;
  onAttach: (file: File) => void;
  onArchive: (documentId: number) => void;
}) {
  /* O que é despesa é do Financeiro; `quitado` e `retido`, também não são da analista. */
  const own = editable && !d.expense && isCompensationDebtStatus(d.status);
  /* O débito antigo pago a um credor (antes de 29/10/2026) ainda pede o comprovante. */
  const legacyPaid = !d.expense && d.creditorCompanyId !== null;
  const facts = [
    d.referenceOn ? `referência ${ddmmyyyy(d.referenceOn)}` : null,
    d.reason
      ? (DEPRECIATION_REASONS[d.reason as keyof typeof DEPRECIATION_REASONS] ?? d.reason)
      : null,
    d.expense
      ? 'pago pelo Financeiro — o detalhe está na aba Despesas'
      : d.creditorName
        ? `pagar a ${d.creditorName}`
        : 'só desconta',
  ].filter(Boolean);
  return (
    <li className="flex flex-col gap-2 border-b border-borda px-3 py-2.5 last:border-b-0">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span
          className={[
            'min-w-0 flex-1 text-sm font-semibold',
            d.counts ? '' : 'text-texto-tenue line-through',
          ].join(' ')}
        >
          {debtName(d)}
        </span>
        {d.expense ? (
          <ExpenseStageTag stage={d.expense.stage} />
        ) : (
          <StatusBadge status={d.status} />
        )}
        <span className="tabular-nums text-sm font-semibold">
          {format(fromColumn(d.amount)!)}
        </span>
      </div>
      <p className="text-xs text-texto-suave">{facts.join(' · ')}</p>
      {d.expense && !d.counts && (
        <p className="text-xs text-texto-suave">
          Recusada: não desconta da indenização.
        </p>
      )}
      {d.notes && <p className="text-xs whitespace-pre-line">{d.notes}</p>}

      {d.receiptFiles.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {d.receiptFiles.map((f) => (
            <li key={f.id} className="flex flex-wrap items-center gap-x-2 text-xs">
              <button
                type="button"
                className={linkTextClasses('min-w-0 truncate text-left')}
                onClick={() =>
                  void claimFileUrl(claimId, f.id).then((url) =>
                    window.open(url, '_blank', 'noopener'),
                  )
                }
              >
                {f.fileName}
              </button>
              <span className="text-texto-tenue">{formatInstant(f.createdAt)}</span>
              {own && legacyPaid && (
                <Button
                  variant="discreto"
                  size="sm"
                  disabled={busy}
                  onClick={() => onArchive(f.id)}
                >
                  Tirar
                </Button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        legacyPaid &&
        d.counts && (
          <p className="text-xs">
            <Badge tone="erro">sem comprovante</Badge>{' '}
            <span className="text-texto-suave">
              trava o Pagamento de Débito enquanto faltar
            </span>
          </p>
        )
      )}

      {own && (
        <div className="flex flex-wrap gap-2">
          {isCompensationDiscountKind(d.kind) && (
            <Button variant="contorno" size="sm" disabled={busy} onClick={onEdit}>
              Corrigir
            </Button>
          )}
          {legacyPaid && (
            <span
              className={buttonClasses({
                variant: 'contorno',
                size: 'sm',
                className: 'relative',
              })}
            >
              <input
                type="file"
                accept={acceptAttribute()}
                aria-label={`Anexar comprovante — ${debtName(d)}`}
                disabled={busy}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  // sem isto, escolher o MESMO arquivo de novo não dispara `change`
                  e.target.value = '';
                  if (file) onAttach(file);
                }}
                className="absolute inset-0 size-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
              />
              Anexar comprovante
            </span>
          )}
          {d.status === 'apurado' && (
            <>
              <Button
                variant="discreto"
                size="sm"
                disabled={busy}
                onClick={() => onStatus('pago_pelo_associado')}
              >
                Pago pelo associado
              </Button>
              <Button
                variant="discreto"
                size="sm"
                disabled={busy}
                onClick={() => onStatus('dispensado')}
              >
                Dispensar
              </Button>
            </>
          )}
          {d.status !== 'apurado' && (
            <Button
              variant="discreto"
              size="sm"
              disabled={busy}
              onClick={() => onStatus('apurado')}
            >
              Voltar a apurado
            </Button>
          )}
        </div>
      )}
    </li>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'apurado') return null;
  if (status === 'dispensado') return <Badge tone="informacao">dispensado</Badge>;
  if (status === 'pago_pelo_associado') {
    return <Badge tone="informacao">pago pelo associado</Badge>;
  }
  if (status === 'quitado') return <Badge tone="sucesso">quitado</Badge>;
  if (status === 'retido') return <Badge tone="alerta">retida · fora da conta</Badge>;
  return <Badge tone="informacao">{status}</Badge>;
}

/**
 * Incluir ou corrigir um DESCONTO — mensalidade, despachante ou depreciação.
 * A regra de cada campo é a da API (`compensationDebtProblem`), conferida aqui
 * antes de enviar: só a depreciação tem — e exige — o motivo. Sem credor: o
 * desconto não se paga a ninguém.
 */
function DiscountForm({
  debt,
  busy,
  error,
  onCancel,
  onSubmit,
}: {
  debt: Debt | null;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (body: CompensationDebtWrite) => void;
}) {
  const initialAmount = debt ? fromColumn(debt.amount) : null;
  const [kind, setKind] = useState(debt?.kind ?? '');
  const [description, setDescription] = useState(debt?.description ?? '');
  const [amount, setAmount] = useState(
    initialAmount === null ? '' : moneyInputText(initialAmount),
  );
  const [referenceOn, setReferenceOn] = useState(debt?.referenceOn ?? '');
  const [reason, setReason] = useState(debt?.reason ?? '');
  const [notes, setNotes] = useState(debt?.notes ?? '');
  const [tried, setTried] = useState(false);

  const parsed = amount.trim() === '' ? null : parseMoneyInput(amount);
  const depreciation = kind === 'depreciacao';
  const problem =
    amount.trim() !== '' && parsed === null
      ? { field: 'amount', message: 'Um valor em reais, como 1.234,56.' }
      : !isCompensationDiscountKind(kind)
        ? { field: 'kind', message: 'Escolha o tipo do desconto.' }
        : compensationDebtProblem({
            kind,
            description: description.trim() || null,
            amount: parsed ?? (0 as Money),
            reason: depreciation ? reason || null : null,
          });
  const errorOf = (field: string) =>
    tried && problem?.field === field ? problem.message : null;

  const submit = () => {
    setTried(true);
    if (problem || parsed === null) return;
    onSubmit({
      kind,
      description: description.trim() || null,
      amount: toColumn(parsed),
      referenceOn: referenceOn || null,
      reason: depreciation ? reason || null : null,
      creditorCompanyId: null,
      notes: notes.trim() || null,
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-texto-suave">
        Desconto não se paga a ninguém: só sai da FIPE. O que se paga a um credor entra em
        Lançar despesa.
      </p>
      {error && <Alert tone="erro">{error}</Alert>}
      <SearchSelect
        label="Tipo"
        required
        options={DISCOUNT_OPTIONS}
        value={kind}
        onChange={setKind}
        error={errorOf('kind')}
      />
      <Field
        label="Descrição"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        maxLength={500}
        error={errorOf('description')}
      />
      <Field
        label="Valor"
        required
        prefix="R$"
        mask="money"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        error={errorOf('amount')}
      />
      {depreciation && (
        <SearchSelect
          label="Motivo da depreciação"
          required
          options={optionsOf(DEPRECIATION_REASONS)}
          value={reason}
          onChange={setReason}
          error={errorOf('reason')}
        />
      )}
      <DateField
        label="Referência"
        layer="modal"
        value={referenceOn}
        onChange={setReferenceOn}
        help="O mês da mensalidade."
      />
      <TextArea
        label="Observações"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        maxLength={4000}
        rows={2}
      />
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="secundario" onClick={onCancel} disabled={busy}>
          Cancelar
        </Button>
        <Button loading={busy} onClick={submit}>
          {debt ? 'Salvar o desconto' : 'Incluir desconto'}
        </Button>
      </div>
    </div>
  );
}

/** "IPVA", ou "Outros — Guincho". */
export function debtName(d: { kind: string; description: string | null }): string {
  const name = deductionKindLabel(d.kind);
  return d.description ? `${name} — ${d.description}` : name;
}

const ddmmyyyy = (iso: string) =>
  `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
